import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  DogPage,
  DogFactsBar,
  DogHealthGenetics,
  RichProfileText
} from "components/Dogs/DogComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";

const agilityIcon = require("../../assets/img/icon-agility.png");
const dockDivingIcon = require("../../assets/img/icon-dockdiving.png");
const flyballIcon = require("../../assets/img/icon-flyball.png");
const stackImage = require("../../assets/img/dogs/rumi/rumi-stack2.jpg");
const pedigreeImage = require("../../assets/img/dogs/litters/nj2025/pedigree.jpg");
const nimonaProfileImage = require("../../assets/img/dogs/profile-nimona.jpg");
const venusProfileImage = require("../../assets/img/dogs/profile-venus.jpg");
const venusHopSingAnnouncementImage = require("../../assets/img/dogs/litters/vh2022/announcement.jpg");

const galleryImages = [
  require("../../assets/img/dogs/rumi/reg1.jpg"),
  require("../../assets/img/dogs/rumi/reg2.jpg"),
  require("../../assets/img/dogs/rumi/reg3.jpg"),
  require("../../assets/img/dogs/rumi/reg4.jpg"),
  require("../../assets/img/dogs/rumi/reg5.jpg"),
  require("../../assets/img/dogs/rumi/reg6.jpg"),
  require("../../assets/img/dogs/rumi/reg7.jpg"),
  require("../../assets/img/dogs/rumi/reg8.jpg"),
  require("../../assets/img/dogs/rumi/reg9.jpg"),
  require("../../assets/img/dogs/rumi/reg10.jpg")
];

const clearances = [
  {
    id: "rumi-embark-ancestry",
    label: "Embark Breed Percents",
    shortLabel: "Breeds",
    category: "Genetic",
    detailLabel: "Breed DNA",
    date: "October 2025",
    image: require("../../assets/img/dogs/rumi/clearance1.jpg"),
    alt: "Rumi Embark breed ancestry summary"
  },
  {
    id: "rumi-embark-health-report-page-1",
    label: "Embark Health Report",
    shortLabel: "Health Report",
    category: "Genetic",
    detailLabel: "DNA",
    date: "October 2025",
    image: require("../../assets/img/dogs/rumi/clearance2.jpg"),
    alt: "Rumi Embark health report breed-relevant results"
  },
  {
    id: "rumi-embark-health-report-page-2",
    label: "Embark Health Continued",
    shortLabel: "DNA Cont.",
    category: "Genetic",
    detailLabel: "DNA",
    date: "October 2025",
    image: require("../../assets/img/dogs/rumi/clearance3.jpg"),
    alt: "Rumi Embark health report continued breed-relevant results"
  },
  {
    id: "rumi-ofa-eyes-2025",
    label: "OFA Eye Certification",
    shortLabel: "Eyes 2025",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "October 2025",
    image: require("../../assets/img/dogs/rumi/clearance4.jpg"),
    alt: "Rumi OFA eye certification from October 2025"
  }
];

const pedigreeData = {
  image: pedigreeImage,
  alt: "Nimona x Jerome pedigree",
  linkedDogs: [
    {
      id: "nimona",
      name: "Nimona",
      relationship: "Dam",
      breed: "Sport Mix",
      image: nimonaProfileImage,
      profileHref: "/dogs/nimona",
      litter: {
        label: "Venus × Hop Sing 2022",
        href: "/litters/venus-hopsing-2022",
        image: venusHopSingAnnouncementImage
      }
    },
    {
      id: "venus",
      name: "Venus",
      relationship: "Granddam",
      breed: "Border Collie",
      image: venusProfileImage,
      profileHref: "/dogs/venus"
    }
  ]
};

const rumiProfile = {
  name: "Rumi",
  registeredName: "Endgame's Gonna Be Golden",
  about: "Rumi is a blue merle tri smooth coat sport mix from the <a href=\"/litters/nimona-jerome\">Nimona x Jerome</a> litter. She will be in training for all the sports once she grows up!",
  facts: [
    { label: "Breed", value: "Sport Mix" },
    { label: "Born", value: "09/06/25" },
    { label: "Height", value: '14.75"' },
    { label: "Weight", value: "18lbs" }
  ],
  temperament: "Rumi takes after her grandmother and loves everyone she meets. She has shown no fear in new environments and works calmly ringside in high arousal situations. She loves other dogs and can play a bit rough, but generally has not had any issues interacting with them. She loves food and toys but leans toward loving food the most.",
  athleticAbility: "Since she is still a puppy, her abilities are still developing but so far she has shown high drive and ground speed. She has debuted in dock diving already with a pb of 21'10 and has begun foundations for agility and flyball. She tends to opt for ground speed over collection and is naturally a wide turning dog.",
  video: {
    src: "https://www.youtube.com/embed/videoseries?si=0lSz2WNxT5oTMRcC&amp;list=PLGEAaz1n-u20"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "TBD" },
      { label: "Elbows", value: "TBD" },
      { label: "Shoulders", value: "TBD" },
      { label: "Eyes", value: "Normal (2025)" },
      { label: "Knees", value: "TBD" },
      { label: "Hearing", value: "BAER Normal" }
    ]
  },
  {
    title: "DNA Panel",
    columns: [
      {
        title: "Breeds",
        items: [
          { label: "Border Collie", value: "44.5%" },
          { label: "Jack Russell", value: "38.6%" },
          { label: "Whippet", value: "11.3%" },
          { label: "Staffy Bull", value: "5.6%" }
        ]
      },
      {
        title: "Border Collie",
        items: [
          { label: "CEA", value: "Clear" },
          { label: "CUBN/IGS", value: "Clear" },
          { label: "DM", value: "Clear" },
          { label: "G/G", value: "Clear" },
          { label: "MDR1", value: "Clear" },
          { label: "NCL5", value: "Clear" },
          { label: "Raine", value: "Clear" },
          { label: "SN", value: "Clear" },
          { label: "TNS", value: "Clear" }
        ]
      },
      {
        title: "Jack Russell Terrier",
        items: [
          { label: "CMS", value: "Clear" },
          { label: "CDDY/IVDD", value: "Clear" },
          { label: "ENAM", value: "Clear" },
          { label: "HUU", value: "Clear" },
          { label: "LOA", value: "Clear" },
          { label: "PLL", value: "Clear" },
          { label: "SCA", value: "Clear" },
          { label: "SCID", value: "Clear" }
        ]
      },
      {
        title: "Other",
        items: [
          { label: "BWS", value: "Clear" },
          { label: "PFK", value: "Clear" },
          { label: "L2HGA", value: "Clear" },
          { label: "MC", value: "Clear" }
        ]
      }
    ]
  }
];

const competitionHighlights = [
  {
    sport: "Dock Diving",
    icon: dockDivingIcon,
    groups: [
      {
        label: "Personal Best",
        items: ["Distance: 21'10"]
      }
    ]
  },
  {
    sport: "Agility",
    icon: agilityIcon,
    groups: [
      {
        label: "Training",
        items: ["Foundation training"]
      }
    ]
  },
  {
    sport: "Flyball",
    icon: flyballIcon,
    groups: [
      {
        label: "Training",
        items: ["Foundation training"]
      }
    ]
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function RumiPage() {
  const hasVideo = Boolean(rumiProfile.video && rumiProfile.video.src);
  return (
    <>
      <SEO
        title="Rumi | Sport Mix Dog"
        description="Meet Rumi, a blue merle tri smooth coat sport mix puppy from the Nimona x Jerome 2025 litter in Raleigh NC, training in dock diving, agility, flyball, and more."
        path="/dogs/rumi"
      />
      <DogPage
        headerBg="rumi"
        pageClassName="dog-detail-page dog-rumi-page"
      >
        <section className="dog-facts-wrap" aria-label="Rumi at a glance">
          <Container>
            <DogFactsBar facts={rumiProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow="Endgame's Gonna be Golden" title={`About ${rumiProfile.name}`} />
                <p><RichProfileText>{rumiProfile.about}</RichProfileText></p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Rumi stacked in profile" src={stackImage} />
                <figcaption>{rumiProfile.stats}</figcaption>
              </figure>
            </div>

            <DogHealthGenetics
              dogName={rumiProfile.name}
              breed={rumiProfile.facts.find(fact => fact.label === "Breed")?.value}
              healthGroups={healthGroups}
              pedigreeData={pedigreeData}
              clearances={clearances}
            />

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{rumiProfile.temperament}</RichProfileText></p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{rumiProfile.athleticAbility}</RichProfileText></p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`Rumi in Action`} title="Sport highlights" />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <div className="video-frame">
                      <iframe
                        src={rumiProfile.video.src}
                        title={rumiProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Rumi is just being her sport training but is having a blast in agility, flyball, and dock diving foundations.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}
          <section className="dog-detail-section dog-competition-section">
            <Container>
              <div className="dog-health-panel-heading dog-competition-panel-heading">
                <h2>Competition Highlights</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-competition-grid">
                {competitionHighlights.map(group => (
                  <section className="dog-sport-highlight" key={group.sport}>
                    <h3>
                      <span className="dog-sport-highlight-icon" aria-hidden="true">
                        <img alt="" src={group.icon} />
                      </span>
                      {group.sport}
                    </h3>
                    {group.groups.map(section => (
                      <div className="dog-sport-highlight-group" key={`${group.sport}-${section.label}`}>
                        <h4>{section.label}</h4>
                        <ul>
                          {section.items.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </Container>
          </section>
        </div>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title="Photo Gallery" />
          </Container>
          <DogGallery images={galleryImages} reverseImages />
        </section>
      </DogPage>
    </>
  );
}

export default RumiPage;
